import type { StatusPillProps, ColorMapping } from "./types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Default colors for unknown/empty values */
const defaultColors: ColorMapping = { bg: "bg-gray-400", text: "text-white" };

/** Gray color for archived options */
const archivedColor = "#9CA3AF";

/**
 * Determines if a hex color is light (for choosing text color).
 * Uses luminance formula: (0.299*R + 0.587*G + 0.114*B) / 255
 */
function isLightColor(hex: string): boolean {
  // Handle shorthand hex (#RGB)
  let normalizedHex = hex;
  if (hex.length === 4) {
    normalizedHex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  const r = parseInt(normalizedHex.slice(1, 3), 16);
  const g = parseInt(normalizedHex.slice(3, 5), 16);
  const b = parseInt(normalizedHex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Displays a color-coded pill/badge for any enum field.
 *
 * This is a generic version that accepts colorMapping as a prop,
 * allowing it to be used for any entity type with status fields.
 *
 * Falls back to gray pill for unknown or empty values.
 * Supports both Tailwind classes (bg/text) and hex colors.
 * Shows gray + italic styling with tooltip for archived options.
 *
 * @example
 * const typeColors = {
 *   Active: { bg: "bg-green-500", text: "text-white" },
 *   Inactive: { bg: "bg-gray-500", text: "text-white", hex: "#6B7280" },
 * };
 * <StatusPill value="Active" colorMapping={typeColors} testIdType="status" />
 */
export function StatusPill({ value, colorMapping, testIdType }: StatusPillProps) {
  const mapping = colorMapping?.[value];
  const isArchived = mapping?.archived ?? false;
  const testId = testIdType ? `pill-${testIdType}-${value}` : undefined;

  // Archived options: gray background, white text, italic
  if (isArchived) {
    const pillContent = (
      <span
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium italic"
        style={{
          backgroundColor: archivedColor,
          color: "#FFFFFF",
        }}
        data-testid={testId}
      >
        {value || "-"}
      </span>
    );

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {pillContent}
          </TooltipTrigger>
          <TooltipContent>
            <p>Wartość zarchiwizowana</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If hex color is provided, use inline styles
  if (mapping?.hex) {
    const isLight = isLightColor(mapping.hex);
    return (
      <span
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
        style={{
          backgroundColor: mapping.hex,
          color: isLight ? "#000000" : "#FFFFFF",
        }}
        data-testid={testId}
      >
        {value || "-"}
      </span>
    );
  }

  // Fallback to Tailwind classes
  const colors = mapping ?? defaultColors;
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
      data-testid={testId}
    >
      {value || "-"}
    </span>
  );
}
